<template>
  <v-card class="criterio-details">
    <span class="criterio-title">
      {{ selectedCriteriaLabel }}
      <v-tooltip v-if="selectedCriteriaTooltip" right max-width="350px">
        <template v-slot:activator="{ on }">
          <span v-on="on">
            <mf-icon small color="cinza-0">info</mf-icon>
          </span>
        </template>
        <span>{{ selectedCriteriaTooltip }}</span>
      </v-tooltip>
    </span>
    <div v-for="(input, index) in currentBranch" :key="index">
      <div v-if="input.component" :class="helperSignalBranchIndex === index ? 'helper-signal' : ''" class="components-container">
        <component
          :is="input.component"
          :key="`${input.treePath}-${forceReloadKey}`"
          :values="input.children"
          :initial-value="input.value"
          :label="input.label"
          :current-index="index"
          :component-params="input.componentParams"
          :current-branch="currentBranch"
          :tooltip="input.tooltip"
          :subtitle="input.subtitle"
          :disabled="disabled"
          :disable-save="disabled"
          :view-only="viewOnly"
          @input="changeInputValue($event)"
          @validate="componentValidated($event)"
          @remove-branch="handleRemoveBranch"
        />
      </div>
    </div>

    <mf-action-buttons
      class="mt-2 px-5"
      :primary-button="{
        text: 'Salvar critério',
        action: saveCriterio,
        isVisible: !disabled,
        isDisabled: !isValid || disabled,
      }"
      :cancel-button="{
        text: cancelButtonText,
        action: cancel,
        isVisible: true,
      }"
    />
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import { leftBranch } from '@/helpers/segmentationTree'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'

export default {
  components: {
    CriterioSelect: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelect.vue'),
    CriterioCheckbox: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioCheckbox.vue'),
    CriterioRadioButton: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioRadioButton.vue'),
    CriterioDate: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioDate.vue'),
    CriterioDateRange: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioDateRange.vue'),
    CriterioFullDateSelect: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioFullDateSelect.vue'),
    CriterioTimeRange: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioTimeRange.vue'),
    CriterioInputProdutos: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioInputProdutos.vue'),
    CriterioInputCategories: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioInputCategories.vue'),
    CriterioSelectLojas: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectLojas.vue'),
    CriterioValueRange: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioValueRange.vue'),
    CriterioValueRangeFloat: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioValueRangeFloat.vue'),
    CriterioImportCustomers: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioImportCustomers.vue'),
    CriterioInputInteger: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioInputInteger.vue'),
    CriterioInputFloat: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioInputFloat.vue'),
    CriterioSelectCampaigns: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectCampaigns.vue'),
    CriterioSelectDynamicAttribute: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectDynamicAttribute.vue'),
    CriterioSelectDynamicAttributeOptions: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectDynamicAttributeOptions.vue'),
    CriterioNoop: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioNoop.vue'),
    CriterioSelectMultiple: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectMultiple.vue'),
    CriterioSelectSegments: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectSegments.vue'),
    CriterioSelectGeolocation: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectGeolocation.vue'),
    CriterioSelectOffers: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectOffers.vue'),
    CriterioSelectAnalyticConfiguration: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectAnalyticConfiguration.vue'),
    CriterioSelectAnalyticPeriod: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectAnalyticPeriod.vue'),
    CriterioSelectBenefits: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectBenefits.vue'),
    CriterioSelectHighlightedOffers: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectHighlightedOffers.vue'),
    CriterioGetHighlightProducts: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioGetHighlightProducts.vue'),
    CriterioGetHighlightDates: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioGetHighlightDates.vue'),
    CriterioSelectUserTags: () => import('@/modules/clientes/segmentation/views/components/criteria/CriterioSelectUserTags.vue'),
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    currentBranch: [],
    validations: [],
    isValid: false,
    helperSignalBranchIndex: null,
    originalCriteria: {},
    selectedCriteriaLabel: '',
    selectedCriteriaTooltip: '',
    forceReloadKey: 0,
  }),

  computed: {
    ...mapState({
      selectedCriteria: (state) => state.segmentation.selectedCriteria,
      segmentation: (state) => state.segmentation.segmentation,
      segmentationGroups: (state) => state.segmentation.segmentationGroups,
    }),

    cancelButtonText() {
      return this.disabled ? 'Fechar' : this.selectedCriteria.new ? 'Remover' : 'Cancelar'
    },
  },

  mounted() {
    // Compute the tree branch based on the criteria values passed
    let currentNode = this.segmentation
    let treePath = []
    this.originalCriteria = cloneDeep(this.selectedCriteria)
    this.selectedCriteria.criteriaValue.forEach((criteria, index) => {
      currentNode = cloneDeep(currentNode.children.find((node) => node.id === criteria.id))
      if (index === 2) {
        this.selectedCriteriaLabel = currentNode.label
        this.selectedCriteriaTooltip = currentNode.tooltip
      }
      if (currentNode) currentNode.value = criteria.value || 0
      treePath.push(criteria.id)
      currentNode.treePath = treePath.join('.')
      this.currentBranch.push(currentNode)
    })
    this.$bus.$on('helperSignal', this.triggerHelperSignal)
  },

  destroyed() {
    this.$bus.$off('helperSignal')
  },

  methods: {
    ...mapActions([
      'setSelectedCriteria',
      'saveSelectedCriteria',
      'deleteSegmentationCriteria',
      'setIsValidSegmentation',
      'setPersistOriginalSize',
      'evaluateSegmentation',
    ]),

    changeInputValue(input) {
      let currentComponent = this.currentBranch[input.currentIndex]
      // This check ensures that events that don't change the current
      // value are ignored
      if (isEqual(input.currentValue, currentComponent.value)) {
        return
      }
      currentComponent.value = input.currentValue

      if (input.affectsChildren) {
        if (!currentComponent.notUpdateChildrens) {
          if (input.forceReloadChildren) {
            this.forceReloadKey += 1
          }

          const leftBranchClone = leftBranch(cloneDeep(input.currentChildren), currentComponent.treePath)
          currentComponent.levelsToPersistChildrenValues?.forEach((levelIndex) => {
            leftBranchClone[levelIndex].value = cloneDeep(this.currentBranch[input.currentIndex + levelIndex + 1].value)
          })
          this.currentBranch.splice(input.currentIndex + 1)
          this.currentBranch = this.currentBranch.concat(leftBranchClone)
        } else {
          if (input.currentChildren) {
            this.currentBranch[input.currentIndex + 1] = {
              ...input.currentChildren,
              isValid: this.currentBranch[input.currentIndex + 1].isValid,
            }
          }
        }
      }
      let selectedCriteria = {
        ...this.selectedCriteria,
        criteriaValue: this.currentBranch.map((component) => {
          return {
            id: component.id,
            value: component.value || 0,
          }
        }),
      }

      this.setSelectedCriteria(selectedCriteria)

      // UPDATE GROUP OF CRITERIONS
      this.segmentationGroups.groups[selectedCriteria.groupIndex].criteria[selectedCriteria.criteriaIndex] = selectedCriteria.criteriaValue
      this.setPersistOriginalSize(false)
    },

    componentValidated(validation) {
      this.currentBranch[validation.currentIndex].isValid = validation.isValid

      // Some components are not rendered to screen.
      // They have component.component = undefined and are always valid (since they can't change)
      // IsValid needs to treat them as valid.

      this.isValid = this.currentBranch.reduce((isValid, component) => isValid && (!component.component || component.isValid), true)
      this.setIsValidSegmentation(this.isValid)
    },

    saveCriterio() {
      this.saveSelectedCriteria()
      this.setSelectedCriteria(null)
    },

    cancel() {
      if (this.selectedCriteria.new) {
        this.deleteSegmentationCriteria({
          groupIndex: this.selectedCriteria.groupIndex,
          criteriaIndex: this.selectedCriteria.criteriaIndex,
        })
      } else {
        // Rollback criteria to initial state!
        this.segmentationGroups.groups[this.originalCriteria.groupIndex].criteria[this.originalCriteria.criteriaIndex] = this.originalCriteria.criteriaValue
        this.evaluateSegmentation({ segment: this.segmentationGroups })
      }

      this.setPersistOriginalSize(false)
      this.setSelectedCriteria(null)
    },
    triggerHelperSignal(branchIndex) {
      this.helperSignalBranchIndex = this.helperSignalBranchIndex === branchIndex ? null : branchIndex
    },
    handleRemoveBranch({ index }) {
      if (index >= 0 && index < this.currentBranch.length) {
        this.currentBranch.splice(index, 1)

        this.updateSelectedCriteriaAfterRemoval()
      }
    },
    updateSelectedCriteriaAfterRemoval() {
      const updatedCriteriaValue = this.currentBranch.map((component) => ({
        id: component.id,
        value: component.value || 0,
      }))

      const updatedCriteria = {
        ...this.selectedCriteria,
        criteriaValue: updatedCriteriaValue,
      }

      this.setSelectedCriteria(updatedCriteria)
    },
  },
}
</script>

<style lang="scss">
.criterio-details {
  border-radius: 10px !important;
  padding: 20px 0;
  max-height: 460px;
  overflow-y: auto;
  overflow-x: hidden;
  .criterio-title {
    font-size: 18px;
    margin: 20px;
  }

  .criterio-subtitle {
    font-size: 16px;
  }

  .v-input {
    &.default-input {
      .v-input__control {
        .v-input__slot {
          min-height: 0 !important;
          height: 36px;
          border: 1px $cinza-l70 solid;
          box-shadow: none !important;

          input {
            text-align: center;
          }
        }
      }
    }
  }

  .components-container {
    padding: 5px 20px;
    border: 2px $branco solid;
  }

  .helper-signal {
    animation-name: helper-signal-animation;
    animation-duration: 4s;

    @keyframes helper-signal-animation {
      0% {
        background-color: $branco;
        border: 2px $branco solid;
        border-radius: 2px;
      }

      15% {
        background-color: $marca-0-l90;
        border: 2px $marca-0-l40 solid;
        border-radius: 2px;
      }

      30% {
        background-color: $branco;
        border: 2px $branco solid;
        border-radius: 2px;
      }
      60% {
        background-color: $marca-0-l90;
        border: 2px $marca-0-l40 solid;
        border-radius: 2px;
      }
      100% {
        background-color: $branco;
        border: 2px $branco solid;
        border-radius: 2px;
      }
    }
  }

  .criterio-subtitle {
    font-size: 16px;
  }

  .v-input {
    &.default-input {
      .v-input__control {
        .v-input__slot {
          min-height: 0 !important;
          height: 36px;
          border: 1px $cinza-l70 solid;
          box-shadow: none !important;

          input {
            text-align: center;
          }
        }
      }
    }
  }

  .components-container {
    padding: 5px 20px;
    border: 2px $branco solid;
  }

  .helper-signal {
    animation-name: helper-signal-animation;
    animation-duration: 4s;

    @keyframes helper-signal-animation {
      0% {
        background-color: $branco;
        border: 2px $branco solid;
      }

      15% {
        background-color: $marca-0-l90;
        border: 2px $marca-0-l40 solid;
        border-radius: 2px;
      }

      30% {
        background-color: $branco;
        border: 2px $branco solid;
        border-radius: 2px;
      }
      60% {
        background-color: $marca-0-l90;
        border: 2px $marca-0-l40 solid;
        border-radius: 2px;
      }
      100% {
        background-color: $branco;
        border: 2px $branco solid;
        border-radius: 2px;
      }
    }
  }
}
</style>
